import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSandboxRunner } from '../runner/SandboxRunnerContext';
import type { TestRunResult, SuiteProgressItem, TestItemState, CollectedTestSuite, PlaygroundStepView, RerunTarget } from '../runner/types';
import type { CoverageReport, ProjectTree } from '@gotti/sandbox-core';
import { declarationLine, assertionLineFromFrames, getFileCode } from './testJumpTargets';
import { PLAYGROUND_CONFIG_PATH } from '../runner/projectPlaygrounds';

type Suite = TestRunResult['suites'][number];
type Test = Suite['tests'][number];

/** A suite the pre-run listing found: a spec file's suite, or one of the project's playgrounds (`collectProjectTests`). */
type DiscoveredSuite = CollectedTestSuite;

/** Whether `path` declares playgrounds: a `*.playground.ts` file, or the config (its inline entries). */
const isPlaygroundFile = (path: string): boolean => /\.playground\.tsx?$/.test(path) || path.replace(/^\/+/, '') === PLAYGROUND_CONFIG_PATH;

/**
 * A signature of what the pre-run listing reads: the spec files, the playground config and every playground definition.
 * Listing compiles them — too expensive to redo on every unrelated edit — so the listing effect is keyed on this and
 * re-runs only when one of them changes.
 */
function projectTestsSignature(project: ProjectTree): string {
  const parts: string[] = [];
  for (const [path, code] of Object.entries(project.files)) {
    if (/\.(spec|test)\.tsx?$/.test(path) || isPlaygroundFile(path)) parts.push(`${path}\0${code}`);
  }
  return parts.join('\x01');
}

interface TestRunnerPanelProps {
  /** The editor's project tree: what the listing reads (its identity changes with every edit). */
  project: ProjectTree;
  /** The project as it is now, its registries up to date — what a run runs. */
  getProject: () => ProjectTree;
  /**
   * Open a file in the Monaco editor, optionally scrolling to a 1-based `line`: a spec by its sandbox path
   * (`shared/__tests__/Game.spec.ts`, `__tests__/x.spec.ts`), a playground by its tree path (`sandbox.playground.ts`).
   * Called for a suite's file button, a test row (its declaration) and a failing assertion (its call-site).
   */
  onOpenFile?: (path: string, line?: number) => void;
}

/**
 * Sidebar panel for browsing, running, and inspecting tests.
 * Discovers tests from source files immediately. Shows run results when available.
 */
export function TestRunnerPanel({ project, getProject, onOpenFile }: TestRunnerPanelProps) {
  const { testResults, testProgress, isRunningTests, coverageReport, runAllTests, rerunTest, cancelTests, collectProjectTests } = useSandboxRunner();
  // Whether a run THIS panel started is in flight: only that run can be cancelled (never an AI-requested one).
  const [panelRunning, setPanelRunning] = useState(false);
  // Playground tests whose steps are shown, by row key — toggled from each test's default (a failed test starts open).
  const [toggledTests, setToggledTests] = useState<Set<string>>(new Set());
  // Track which suites have been *collapsed* (default: all expanded). Using a
  // collapsed-set inverts the prior expanded-set + "size === 0 means all open"
  // fallback, which caused the wrong rows to flip when the first toggle landed.
  const [collapsedSuites, setCollapsedSuites] = useState<Set<string>>(new Set());
  const [selectedTest, setSelectedTest] = useState<string | null>(null);

  // Pre-run preview: collect the suites/tests the specs REGISTER (without
  // running them). Done by loading the files via `collectTests` — accurate for
  // `defineSandboxTest` and helper/factory-produced tests, which a source-regex
  // scan can't see. Keyed on a signature of the test files so we only re-collect
  // (which compiles) when a spec actually changes; `systems` is read fresh via a
  // ref so identity churn doesn't trigger needless recompiles.
  const [discoveredSuites, setDiscoveredSuites] = useState<DiscoveredSuite[]>([]);
  // Re-list when the project's test files or playgrounds change — the AI's included: it writes into the tree.
  const sig = useMemo(() => projectTestsSignature(project), [project]);
  const projectRef = useRef(project);
  projectRef.current = project;
  useEffect(() => {
    let cancelled = false;
    const id = setTimeout(() => {
      collectProjectTests(projectRef.current)
        .then(suites => { if (!cancelled) setDiscoveredSuites(suites); })
        .catch(() => { if (!cancelled) setDiscoveredSuites([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(id); };
  }, [sig, collectProjectTests]);

  /**
   * Lookup table from suite name → spec file path. The discovery pass
   * stores `file` like "client/__tests__/Game.spec" (no extension). We
   * tack `.ts` back on so it matches the editor's path convention.
   * Result suites don't carry the file path, so we resolve by name.
   */
  const fileBySuiteName = useMemo(() => {
    const m = new Map<string, string>();
    for (const ds of discoveredSuites) {
      const path = ds.file.endsWith('.ts') ? ds.file : `${ds.file}.ts`;
      m.set(ds.name, path);
    }
    return m;
  }, [discoveredSuites]);

  const openSuiteFile = useCallback(
    (suiteName: string) => {
      const path = fileBySuiteName.get(suiteName);
      if (path && onOpenFile) onOpenFile(path);
    },
    [fileBySuiteName, onOpenFile],
  );

  /**
   * Open a spec at a clicked test row → jump to where that test is declared.
   * `systems` is read via the ref so the lookup always sees current source.
   */
  const jumpToTest = useCallback(
    (path: string | undefined, testName: string) => {
      if (!path || !onOpenFile) return;
      const code = getFileCode(projectRef.current, path);
      const line = code ? declarationLine(code, testName) : undefined;
      onOpenFile(path, line);
    },
    [onOpenFile],
  );

  /**
   * Open a spec at a clicked assertion → jump to the failing call-site from
   * the error's stack frames, falling back to the test declaration when no
   * frame points into the spec (e.g. a load/setup error with no spec frame).
   */
  const jumpToAssertion = useCallback(
    (path: string | undefined, testName: string, errorFrames: string[] | undefined) => {
      if (!path || !onOpenFile) return;
      const code = getFileCode(projectRef.current, path);
      const line =
        assertionLineFromFrames(errorFrames, path) ??
        (code ? declarationLine(code, testName) : undefined);
      onOpenFile(path, line);
    },
    [onOpenFile],
  );

  const handleRunAll = useCallback(async () => {
    setPanelRunning(true);
    try {
      await runAllTests(getProject());
    } finally {
      setPanelRunning(false);
    }
  }, [getProject, runAllTests]);

  const handleRerun = useCallback(async (target: RerunTarget) => {
    setPanelRunning(true);
    try {
      await rerunTest(getProject(), target);
    } finally {
      setPanelRunning(false);
    }
  }, [getProject, rerunTest]);

  /** The discovered suite of this name in this file — two spec files may declare one suite name; a playground may share it. */
  const discoveredSuite = useCallback(
    (name: string, file: string | undefined) => discoveredSuites.find(
      (suite) => suite.name === name && (file === undefined || suite.file.replace(/^\/+/, '') === file.replace(/^\/+/, '')),
    ),
    [discoveredSuites],
  );

  /**
   * What rerunning a test row runs, or null when it cannot rerun: a playground's test by its id — a result that carries
   * a playground step tree is one, as is a test the listing found under a playground suite of the same file — else a
   * spec file's test by suite and name. A file's load-error row has none: Run All loads the file again.
   */
  const rerunTargetOf = useCallback((suiteName: string, file: string | undefined, test: { name: string; id?: string; playgroundId?: string; loadError?: boolean }): RerunTarget | null => {
    if (test.loadError) return null;
    const discovered = discoveredSuite(suiteName, file);
    const playground = discovered?.kind === 'playground' ? discovered : undefined;
    const testId = test.playgroundId ?? (playground ? test.id ?? playground.tests.find((listed) => listed.name === test.name)?.id : undefined);
    // A step tree from a spec file is a `defineSandboxTest` test's (it ran on the playground's runner): the test is the
    // spec's, and reruns as one.
    const playgroundFile = playground?.file ?? (test.playgroundId && file && isPlaygroundFile(file) ? file : undefined);
    if (playgroundFile && testId) {
      return {
        kind: 'playground',
        file: playgroundFile,
        playground: suiteName,
        ...(playground?.playground?.member === undefined ? {} : { member: playground.playground.member }),
        ...(playground?.playground?.variant === undefined ? {} : { variant: playground.playground.variant }),
        testId,
        testLabel: test.name,
      };
    }
    return file ? { kind: 'spec', file, suiteName, testName: test.name } : null;
  }, [discoveredSuite]);

  /** Whether a playground test's steps are shown: a failed one starts open, any can be toggled. */
  const isTestOpen = useCallback(
    (testKey: string, status: TestItemState) => toggledTests.has(testKey) !== (status === 'failed' || status === 'error'),
    [toggledTests],
  );
  const toggleTest = useCallback((testKey: string) => {
    setToggledTests((previous) => {
      const next = new Set(previous);
      if (next.has(testKey)) next.delete(testKey);
      else next.add(testKey);
      return next;
    });
  }, []);

  const toggleSuite = useCallback((suiteKey: string) => {
    setCollapsedSuites(prev => {
      const next = new Set(prev);
      if (next.has(suiteKey)) next.delete(suiteKey);
      else next.add(suiteKey);
      return next;
    });
  }, []);

  const hasResults = testResults && testResults.totalTests > 0;
  const hasDiscovered = discoveredSuites.length > 0;
  const showEmpty = !isRunningTests && !hasResults && !hasDiscovered;

  // A run is in flight when either the action flag is set OR the streaming
  // progress is mid-run. While running with live suites, we render those
  // (pending → running → passed/failed) so a RERUN shows real pending states
  // instead of freezing on the previous results or blanking out — the panel
  // otherwise only learns of a run when its final `testResults` land.
  const isRunning = isRunningTests || testProgress.phase === 'running';
  // Drive the live tree strictly off the streaming phase so the instant a rerun
  // starts (isRunningTests flips true but progress hasn't reset yet) we don't
  // flash the PREVIOUS run's completed suites through the streaming view.
  const showStreaming = testProgress.phase === 'running' && testProgress.suites.length > 0;

  // Merge: if we have run results, use them. Otherwise show discovered (pending).
  const displaySuites = useMemo(() => {
    if (hasResults) {
      // Use actual results, but also include any discovered suites not yet in results
      const resultNames = new Set(testResults!.suites.map(s => s.name));
      const merged: Array<{ type: 'result'; suite: Suite } | { type: 'discovered'; suite: DiscoveredSuite }> = [];
      for (const suite of testResults!.suites) {
        merged.push({ type: 'result', suite });
      }
      for (const ds of discoveredSuites) {
        if (!resultNames.has(ds.name)) {
          merged.push({ type: 'discovered', suite: ds });
        }
      }
      return merged;
    }
    // No results yet — show all discovered as pending
    return discoveredSuites.map(suite => ({ type: 'discovered' as const, suite }));
  }, [hasResults, testResults, discoveredSuites]);

  // Count totals for the summary badge
  const totalTests = hasResults ? testResults!.totalTests : discoveredSuites.reduce((n, s) => n + s.tests.length, 0);
  const totalPassed = hasResults ? testResults!.totalPassed : 0;
  const totalFailed = hasResults ? testResults!.totalFailed : 0;

  return (
    <div className="test-runner-panel" data-testid="test-runner-panel">
      {/* Toolbar */}
      <div className="test-runner-toolbar">
        <button
          className="test-runner-run-all"
          onClick={handleRunAll}
          disabled={isRunningTests}
          title="Run All Tests"
          data-testid="test-runner-run-all"
        >
          {isRunningTests ? (
            <span className="test-runner-spinner">⟳</span>
          ) : (
            <PlayIcon />
          )}
          <span>{isRunningTests ? 'Running...' : 'Run All'}</span>
        </button>
        {panelRunning && isRunning && (
          <button
            type="button"
            className="test-runner-cancel"
            onClick={cancelTests}
            title="Cancel this run"
            data-testid="test-runner-cancel"
          >
            Cancel
          </button>
        )}
        {hasResults && (
          <span className="test-runner-summary">
            <span className={totalFailed === 0 ? 'test-pass' : 'test-fail'}>
              {totalPassed}/{totalTests}
            </span>
            {testResults!.cancelled && ' (cancelled)'}
            {testResults!.rerun && ` — reran “${testResults!.rerun.testName}”`}
          </span>
        )}
      </div>

      {/* Empty state — no test files at all */}
      {showEmpty && (
        <div className="test-runner-empty">
          <div className="test-runner-empty-icon">
            <TestTubeIcon />
          </div>
          <p>No tests found</p>
          <p className="test-runner-empty-hint">
            Add test files or let the AI agent write tests
          </p>
        </div>
      )}

      {/* Running state — shown until the first suite registers (then the live
          streaming tree below takes over). Gives an immediate signal on RERUN
          rather than leaving the stale rows looking frozen. */}
      {isRunning && !showStreaming && (
        <div className="test-runner-loading">
          <span className="test-runner-spinner">⟳</span>
          <span>Running tests...</span>
        </div>
      )}

      {/* Live run view: each test as pending → running → passed/failed. */}
      {showStreaming && (
        <div className="test-runner-tree" data-testid="test-runner-tree">
          {testProgress.suites.map((suite, si) => {
            const suiteKey = `${suite.name}-${si}`;
            return (
              <StreamingSuiteRow
                key={suiteKey}
                suite={suite}
                suiteKey={suiteKey}
                isExpanded={!collapsedSuites.has(suiteKey)}
                onToggle={toggleSuite}
              />
            );
          })}
        </div>
      )}

      {/* Static tree — discovered preview (pending) or completed results. Hidden
          while the live streaming tree is up so the two don't double-render. */}
      {!showStreaming && displaySuites.length > 0 && (
        <div className="test-runner-tree" data-testid="test-runner-tree">
          {displaySuites.map((entry, si) => {
            const suiteKey = `${entry.suite.name}-${si}`;
            const isExpanded = !collapsedSuites.has(suiteKey);

            if (entry.type === 'result') {
              const suite = entry.suite as Suite;
              const allPassed = suite.failed === 0;

              // Prefer the suite's own `file` (the run now tags results with it,
              // see executeTests) so jump-to-source works even before the
              // debounced discovery map has populated; fall back to discovery.
              const resultFilePath = suite.file ?? fileBySuiteName.get(suite.name);
              return (
                <div key={suiteKey} className="test-suite-node" data-testid="test-suite-node">
                  <div className="test-suite-header" onClick={() => toggleSuite(suiteKey)}>
                    <span className={`test-suite-chevron ${isExpanded ? 'expanded' : ''}`}>▶</span>
                    <StatusIcon status={allPassed ? 'passed' : 'failed'} />
                    <span className="test-suite-name">{suite.name}</span>
                    <span className="test-suite-count">{suite.passed}/{suite.tests.length}</span>
                    {resultFilePath && onOpenFile && (
                      <button
                        type="button"
                        className="test-suite-open-btn"
                        title={`Open ${resultFilePath} in the editor`}
                        onClick={e => {
                          e.stopPropagation();
                          openSuiteFile(suite.name);
                        }}
                        data-testid="test-suite-open-file"
                        data-file-path={resultFilePath}
                      >
                        {resultFilePath.split('/').pop()}
                      </button>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="test-suite-children">
                      {suite.tests.map((test: Test, ti: number) => {
                        const testKey = `${suiteKey}::${test.name}-${ti}`;
                        const isSelected = selectedTest === testKey;
                        const steps = test.playground?.steps;
                        const open = !!steps && isTestOpen(testKey, test.status);
                        const target = rerunTargetOf(suite.name, test.file ?? resultFilePath, { name: test.name, playgroundId: test.playground?.id, loadError: test.name === 'load error' && test.error?.name === 'LoadError' });
                        return (
                          <div key={testKey}>
                            <div
                              className={`test-item-node ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedTest(testKey);
                                jumpToTest(resultFilePath, test.name);
                              }}
                              title="Open this test in the editor"
                              data-testid="test-item-node"
                              data-status={test.status}
                            >
                              {steps && (
                                <button
                                  type="button"
                                  className={`test-item-expand test-suite-chevron ${open ? 'expanded' : ''}`}
                                  title={open ? 'Hide its steps' : 'Show its steps'}
                                  onClick={e => { e.stopPropagation(); toggleTest(testKey); }}
                                  data-testid="test-item-expand"
                                >
                                  ▶
                                </button>
                              )}
                              <StatusIcon status={test.status} />
                              <span className="test-item-name">{test.name}</span>
                              {/* Optional — the engine keeps per-test durations
                                  out of the compared result (see runner/types.ts). */}
                              {test.duration != null && test.duration > 0 && (
                                <span className="test-item-duration">{test.duration.toFixed(0)}ms</span>
                              )}
                              {!isRunning && target && (
                                <button
                                  type="button"
                                  className="test-item-rerun"
                                  title="Run this test again"
                                  onClick={e => { e.stopPropagation(); void handleRerun(target); }}
                                  data-testid="test-item-rerun"
                                >
                                  ↻
                                </button>
                              )}
                            </div>
                            {open && <StepRows steps={steps!} />}
                          </div>
                        );
                      })}
                      {/* The suite's tests the last run did not run — a rerun before any Run All, a cancelled run — still
                          listed, as pending. */}
                      {(discoveredSuite(suite.name, resultFilePath)?.tests ?? [])
                        .filter((listed) => !suite.tests.some((ran: Test) => ran.name === listed.name))
                        .map((listed, pi) => {
                          const target = rerunTargetOf(suite.name, resultFilePath, listed);
                          return (
                            <div
                              key={`${suiteKey}::pending::${listed.name}-${pi}`}
                              className="test-item-node"
                              onClick={() => jumpToTest(resultFilePath, listed.name)}
                              title="Open this test in the editor"
                              data-testid="test-item-node"
                              data-status="pending"
                            >
                              <StatusIcon status="pending" />
                              <span className="test-item-name">{listed.name}</span>
                              {!isRunning && target && (
                                <button
                                  type="button"
                                  className="test-item-rerun"
                                  title="Run this test"
                                  onClick={e => { e.stopPropagation(); void handleRerun(target); }}
                                  data-testid="test-item-rerun"
                                >
                                  ↻
                                </button>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                  {isExpanded && suite.tests.some((t: Test) => t.error?.message) && (
                    <div className="test-suite-errors">
                      {suite.tests
                        .filter((t: Test) => t.error?.message)
                        .map((t: Test, ei: number) => (
                          <div
                            key={ei}
                            className="test-error-block test-error-clickable"
                            onClick={() => jumpToAssertion(resultFilePath, t.name, t.error?.errorFrames)}
                            role="button"
                            tabIndex={0}
                            title="Jump to this assertion in the editor"
                          >
                            <span className="test-error-label">✗ {t.name}</span>
                            <pre className="test-error-message">{t.error!.message}</pre>
                            {t.logs?.map((log, li) => (
                              <div key={li} className="test-item-log">{`${log.count > 1 ? `(${log.count}×) ` : ''}${log.message}`}</div>
                            ))}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            }

            // Discovered (pending) suite — not yet executed
            const ds = entry.suite as DiscoveredSuite;
            const dsPath = /\.tsx?$/.test(ds.file) ? ds.file : `${ds.file}.ts`;
            return (
              <div key={suiteKey} className="test-suite-node" data-testid="test-suite-node">
                <div className="test-suite-header" onClick={() => toggleSuite(suiteKey)}>
                  <span className={`test-suite-chevron ${isExpanded ? 'expanded' : ''}`}>▶</span>
                  <StatusIcon status="pending" />
                  <span className="test-suite-name">{ds.name}</span>
                  <span className="test-suite-count">{ds.tests.length}</span>
                  {onOpenFile && (
                    <button
                      type="button"
                      className="test-suite-open-btn"
                      title={`Open ${dsPath} in the editor`}
                      onClick={e => {
                        e.stopPropagation();
                        onOpenFile(dsPath);
                      }}
                      data-testid="test-suite-open-file"
                      data-file-path={dsPath}
                    >
                      {dsPath.split('/').pop()}
                    </button>
                  )}
                </div>
                {isExpanded && ds.tests.length > 0 && (
                  <div className="test-suite-children">
                    {ds.tests.map((test, ti) => {
                      const testKey = `${suiteKey}::${test.name}-${ti}`;
                      const target = rerunTargetOf(ds.name, dsPath, test);
                      return (
                        <div
                          key={testKey}
                          className="test-item-node"
                          onClick={() => jumpToTest(dsPath, test.name)}
                          title="Open this test in the editor"
                          data-testid="test-item-node"
                          data-status="pending"
                        >
                          <StatusIcon status="pending" />
                          <span className="test-item-name">{test.name}</span>
                          {!isRunning && target && (
                            <button
                              type="button"
                              className="test-item-rerun"
                              title="Run this test"
                              onClick={e => { e.stopPropagation(); void handleRerun(target); }}
                              data-testid="test-item-rerun"
                            >
                              ↻
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Coverage report */}
      {coverageReport && coverageReport.files.length > 0 && (
        <CoverageSection report={coverageReport} />
      )}

      {/* Duration footer */}
      {hasResults && (
        <div className="test-runner-footer">
          {testResults!.duration.toFixed(0)}ms
        </div>
      )}
    </div>
  );
}

// ── Coverage ────────────────────────────────────────────────────────────────

function CoverageSection({ report }: { report: CoverageReport }) {
  const [expanded, setExpanded] = useState(false);

  const barColor = report.percentage >= 80 ? 'var(--test-pass-color, #4caf50)'
    : report.percentage >= 50 ? 'var(--test-warn-color, #ff9800)'
    : 'var(--test-fail-color, #f44336)';

  return (
    <div className="coverage-section" data-testid="coverage-section">
      <div className="coverage-header" onClick={() => setExpanded(e => !e)}>
        <span className={`test-suite-chevron ${expanded ? 'expanded' : ''}`}>▶</span>
        <span className="coverage-label">Coverage</span>
        <span className="coverage-pct" style={{ color: barColor }}>
          {report.percentage}%
        </span>
        <span className="coverage-summary">
          {report.coveredLines}/{report.totalLines} lines
        </span>
      </div>
      <div className="coverage-bar-track">
        <div
          className="coverage-bar-fill"
          style={{ width: `${report.percentage}%`, backgroundColor: barColor }}
        />
      </div>
      {expanded && (
        <div className="coverage-files">
          {report.files.map(file => (
            <CoverageFileRow key={file.path} file={file} />
          ))}
        </div>
      )}
    </div>
  );
}

function CoverageFileRow({ file }: { file: CoverageReport['files'][number] }) {
  const color = file.percentage >= 80 ? 'var(--test-pass-color, #4caf50)'
    : file.percentage >= 50 ? 'var(--test-warn-color, #ff9800)'
    : 'var(--test-fail-color, #f44336)';

  return (
    <div className="coverage-file-row">
      <span className="coverage-file-name" title={file.path}>
        {file.path.replace(/^\//, '')}
      </span>
      <div className="coverage-file-bar-track">
        <div
          className="coverage-file-bar-fill"
          style={{ width: `${file.percentage}%`, backgroundColor: color }}
        />
      </div>
      <span className="coverage-file-pct" style={{ color }}>
        {file.percentage}%
      </span>
    </div>
  );
}

// ── Streaming (in-progress) suite row ─────────────────────────────────────────

/**
 * One suite rendered from the live `testProgress` stream while a run is in
 * flight. Mirrors the static result-suite markup (same CSS classes / testids)
 * but its tests carry transient states — `pending` (○) and `running` (spinner)
 * — so a rerun visibly steps each test through its states instead of showing
 * stale results. Errors/jumps are intentionally omitted here; they belong to
 * the completed-results view (only `testResults` carries `errorFrames`).
 */
function StreamingSuiteRow({ suite, suiteKey, isExpanded, onToggle }: {
  suite: SuiteProgressItem;
  suiteKey: string;
  isExpanded: boolean;
  onToggle: (key: string) => void;
}) {
  const isDone = (s: TestItemState) => s === 'passed' || s === 'failed' || s === 'error' || s === 'skipped';
  const done = suite.tests.filter(t => isDone(t.state)).length;
  return (
    <div className="test-suite-node" data-testid="test-suite-node">
      <div className="test-suite-header" onClick={() => onToggle(suiteKey)}>
        <span className={`test-suite-chevron ${isExpanded ? 'expanded' : ''}`}>▶</span>
        <StatusIcon status={suite.state} />
        <span className="test-suite-name">{suite.name}</span>
        <span className="test-suite-count">{done}/{suite.tests.length}</span>
      </div>
      {isExpanded && (
        <div className="test-suite-children">
          {suite.tests.map((test, ti) => (
            <div
              key={`${test.name}-${ti}`}
              className="test-item-node"
              data-testid="test-item-node"
              data-status={test.state}
            >
              <StatusIcon status={test.state} />
              <span className="test-item-name">{test.name || '…'}</span>
              {test.duration != null && isDone(test.state) && (
                <span className="test-item-duration">{test.duration.toFixed(0)}ms</span>
              )}
              {test.state === 'running' && test.steps && <StepRows steps={test.steps} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── A playground test's steps ─────────────────────────────────────────────────

/**
 * A playground test's steps, each with its time, its errors, the console lines it produced and every check (assertion)
 * its sides ran. Rows here never carry `test-item-node` or `test-suite-node`: the e2e specs count those page-wide.
 */
function StepRows({ steps }: { steps: PlaygroundStepView[] }) {
  return (
    <div className="test-steps">
      {steps.map((step, index) => (
        <div key={`${step.label}-${index}`} className="test-step-node" data-testid="test-step-node" data-status={step.state}>
          <div className="test-step-header">
            <StatusIcon status={step.state} />
            <span className="test-step-label">{step.label}</span>
            <span className="test-step-time">@{step.atTime} ms</span>
            {step.durationMs != null && <span className="test-item-duration">{step.durationMs.toFixed(0)}ms</span>}
          </div>
          {step.errors.map((error, ei) => (
            <pre key={`e${ei}`} className="test-step-error">{error}</pre>
          ))}
          {step.logs.map((line, li) => (
            <div key={`l${li}`} className="test-step-log">{line}</div>
          ))}
          {step.checks.map((check, ci) => (
            <div key={`c${ci}`} className="test-check-node" data-testid="test-check-node" data-status={check.state}>{`${check.side} ${check.phase} › ${check.label}`}</div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Icons ───────────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: TestItemState | PlaygroundStepView['state'] }) {
  if (status === 'passed') {
    return <span className="test-status-icon test-pass">✓</span>;
  }
  if (status === 'pending') {
    return <span className="test-status-icon test-pending">○</span>;
  }
  if (status === 'running') {
    return <span className="test-status-icon test-runner-spinner">⟳</span>;
  }
  if (status === 'skipped') {
    return <span className="test-status-icon test-pending">⊘</span>;
  }
  return <span className="test-status-icon test-fail">✗</span>;
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5,3 19,12 5,21" />
    </svg>
  );
}

function TestTubeIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5s-2.5-1.1-2.5-2.5V2" />
      <path d="M8.5 2h7" />
      <path d="M14.5 16h-5" />
    </svg>
  );
}
